import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tab",
    "pickedInstructorToken",
    "radio",
    "programId",
    "startTime",
    "availableToTeens",
    "teenMinimumAge",
    "teenMinimumAgeYears",
    "teenMinimumAgeMonths",
    "availableToAdults",
    "teensCanScheduleOnline",
    "adultsCanScheduleOnline",
    "availableStudentProgram",
    "descriptionSummary",
    "descriptionAgenda",
    "descriptionPrivateNotes",
    "requireSignatureAfterEachSession",
    "driveTestCourseworkThreshold",
    "driveTestMinimumCourseworkScorePercent",
    "driveTestSchedulingLimitMode",
    "daysSessionsRun",
    "maxInPersonAttendees",
    "maxInPersonMakeUp",
    "maxRemoteAttendees",
    "maxRemoteMakeUp",
    "remoteLink",
    "validationBlock",
    "validationMessage",
    "saveButton",
    "confirmPanel",
    "confirmMessage",
    "studentProgramSection",
    "studentProgramRadio",
    "studentProgramOption",
    "programOptions",
    "enterCodeContainer",
    "codeField",
    "introductoryMeetingsSection",
    "introductoryMeetingsForProgram",
    "introductoryMeetingRadio",
    "introductoryMeetingAttendanceFields",
    "navigationId",
    "navigationIdError",
    "navigationIdSuccess",
    "navigationIdLoading",
    "enrollmentWarning",
    "enrollmentWarningList",
    "enrollmentSaveButton",
    "selectedProgramInput"
  ];

  static values = {
    minRows: { type: Number, default: 1 },
    paramName: String,
    programId: Number,
    originalNavigationId: String,
    pupilId: String
  }

  connect() {
    this._courseworkRemoveRequest = null;
    this._initialCourseworkById = this.initialCourseworkById();
    this.navigationIdInUse = false;
    this.navigationIdCheckTimeout = null;
    this.navigationIdCheckIdForThrottling = null;
    this.toggleTeenMinimumAge();
    this.toggleDriveTestCourseworkThreshold();

    if (this.hasIntroductoryMeetingsSectionTarget && this.hasRadioTarget) {
      const firstRadio = this.radioTargets.find(radio => radio.checked);
      if (firstRadio) {
        const event = { target: firstRadio };
        this.updateIntroductoryMeetingsSection(event);
      }
    }
    
    if (this.hasStudentProgramSectionTarget && this.hasRadioTarget) {
      const firstRadio = this.radioTargets.find(radio => radio.checked);
      if (firstRadio) {
        const event = { target: firstRadio };
        this.updateStudentProgramSection(event);
      }
    }

    // When using the dropdown widget (no radio targets), seed initial state from the hidden input.
    if (!this.hasRadioTarget && this.hasSelectedProgramInputTarget) {
      const initialId = this.selectedProgramInputTarget.value;
      if (initialId) {
        const fakeEvent = { target: { value: initialId } };
        if (this.hasIntroductoryMeetingsSectionTarget) this.updateIntroductoryMeetingsSection(fakeEvent);
        if (this.hasStudentProgramSectionTarget) this.updateStudentProgramSection(fakeEvent);
      }
    }

    // Enrollment check runs only when a student has been actively selected.
    // The pupilIdValue is populated by the frame reloading after student selection,
    // so if it is present here the student was already chosen — run the check.
    if (this.pupilIdValue) {
      this.checkExistingEnrollment();
    }

    this._onClick = (event) => {
      // Did we click a coursework remove button?
      const removeBtn = event.target.closest('button[data-action*="add-to-form#remove"]');
      if (!removeBtn) return;

      if (removeBtn.dataset.skipConfirm) return;

      const addToFormContainer = removeBtn.closest('[data-controller~="add-to-form"]');
      if (!addToFormContainer) return;

      const addToFormRow = removeBtn.closest('[data-add-to-form-target="row"]');
      if (!addToFormRow) return;

      const addToFormController = this.application.getControllerForElementAndIdentifier(addToFormContainer, "add-to-form");
      if (addToFormController && addToFormController.rowTargets.length <= addToFormController.minRowsValue) return;

      // Stop the add-to-form controller from handling the click
      event.preventDefault();
      event.stopImmediatePropagation();

      this.hideValidationBlock();

      const message = "Removing a coursework item will remove all of its associated scores. Continue?";

      this.showConfirmPanelForRow(addToFormRow, addToFormController, message);
    }

    this.element.addEventListener("click", this._onClick, true);
  }

  toggleTeenMinimumAge() {
    if (!this.hasTeenMinimumAgeTarget || !this.hasAvailableToTeensTarget) return;

    this.teenMinimumAgeTarget.classList.toggle("u-hidden", !this.availableToTeensTarget.checked);
  }

  toggleDriveTestCourseworkThreshold() {
    if (!this.hasDriveTestCourseworkThresholdTarget) return;

    const shouldShow = this.driveTestSchedulingLimitedByCoursework();

    this.driveTestCourseworkThresholdTarget.classList.toggle("u-hidden", !shouldShow);

    if (this.hasDriveTestMinimumCourseworkScorePercentTarget) {
      this.driveTestMinimumCourseworkScorePercentTarget.disabled = !shouldShow;
    }
  }

  disconnect() {
    if (this._onClick) this.element.removeEventListener("click", this._onClick, true);
  }

  initialCourseworkById() {
    const map = new Map();

    const addToFormContainer = this.courseworkAddToFormContainer();
    if (!addToFormContainer) return map;

    const rows = addToFormContainer.querySelectorAll('[data-add-to-form-target="container"] [data-add-to-form-target="row"]');

    rows.forEach((row) => {
      const id = (row.querySelector('input[name$="[id]"]')?.value || "").trim();
      if (!id) return;

      const name = (row.querySelector('input[name$="[name]"]')?.value || "").trim();
      const weight = (row.querySelector('input[name$="[weight]"]')?.value || "").trim();

      map.set(id, { name, weight });
    });

    return map;
  }

  submit(event) {
    event.preventDefault();
    const selectedProgram = this.getSelectedProgram();
    const pupilId = this.getPupilId();
    
    if (selectedProgram && pupilId) {
      this.postForm(selectedProgram, pupilId);
    } else {
        console.error('No program selected');
    }
  }

  getSelectedProgram() {
    if (this.hasSelectedProgramInputTarget) {
      return this.selectedProgramInputTarget.value || null;
    }
    const selectedRadio = this.radioTargets.find(radio => radio.checked);
    return selectedRadio ? selectedRadio.value : null;
  }

  programSelected(event) {
    const programId = String(event.detail?.dispatchItem?.id || event.detail?.id || "");
    if (!programId) return;

    const fakeEvent = { target: { value: programId } };
    if (this.hasIntroductoryMeetingsSectionTarget) this.updateIntroductoryMeetingsSection(fakeEvent);
    if (this.hasStudentProgramSectionTarget) this.updateStudentProgramSection(fakeEvent);
  }

  getPupilId() {
    const hiddenField = document.getElementById("pupil_id");
    if (hiddenField) {
      return hiddenField.value;
    }
    
    const selectedPupilRadio = document.querySelector('input[name="selected-item-pupil"]:checked');
    if (selectedPupilRadio) {
      return selectedPupilRadio.value;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('picked_pupil') || urlParams.get('pupil_id');
  }

  updateStudentProgramSection(event) {
    if (!this.hasStudentProgramSectionTarget) return;
    
    const selectedProgramId = event.target.value;
    const section = this.studentProgramSectionTarget;
    
    const programsMap = JSON.parse(section.dataset.studentProgramsMap || '{}');
    const availableStudentProgramIds = programsMap[selectedProgramId] || [];
    
    if (availableStudentProgramIds.length > 0) {
      section.classList.remove('u-hidden');
      
      const options = section.querySelectorAll('[data-student-program-id]');
      options.forEach(option => {
        const programId = parseInt(option.dataset.studentProgramId);
        if (availableStudentProgramIds.includes(programId)) {
          option.classList.remove('u-hidden');
        } else {
          option.classList.add('u-hidden');
          const radio = option.querySelector('input[type="radio"]');
          if (radio && radio.checked) {
            const noneRadio = section.querySelector('#student_program_none');
            if (noneRadio) noneRadio.checked = true;
          }
        }
      });
      
      this.programOptionsTargets.forEach(options => {
        options.hidden = true;
        const checkbox = options.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = false;
        const codeField = options.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
        if (codeField) codeField.value = '';
        const codeContainer = options.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
        if (codeContainer) codeContainer.hidden = true;
      });
      
      const checkedRadio = section.querySelector('input[name="selected_student_program"]:checked');
      if (checkedRadio && checkedRadio.value) {
        this.updateStudentProgramOptions({ target: checkedRadio });
      }
    } else {
      section.classList.add('u-hidden');
      const noneRadio = section.querySelector('#student_program_none');
      if (noneRadio) noneRadio.checked = true;
    }
  }

  updateStudentProgramOptions(event) {
    if (!this.hasStudentProgramSectionTarget) return;
    
    const section = this.studentProgramSectionTarget;
    const selectedStudentProgramId = event.target.value;
    
    this.programOptionsTargets.forEach(options => {
      options.hidden = true;
      const checkbox = options.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
      if (checkbox) checkbox.checked = false;
      const codeField = options.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
      if (codeField) codeField.value = '';
      const codeContainer = options.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
      if (codeContainer) codeContainer.hidden = true;
    });
    
    if (!selectedStudentProgramId) return;
    
    const programOptions = Array.from(this.programOptionsTargets).find(options => {
      return options.dataset.studentProgramId === selectedStudentProgramId;
    });
    
    if (programOptions) {
      programOptions.hidden = false;
    }
  }

  toggleCodeField(event) {
    const financialScholarshipCheckbox = event.target;
    if (financialScholarshipCheckbox.type !== 'checkbox') return;
    
    const programOptions = financialScholarshipCheckbox.closest('[data-driving-school-classroom-quick-look-target="programOptions"]');
    if (!programOptions) return;
    
    const enterCodeContainer = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
    const codeField = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
    
    if (!enterCodeContainer) return;
    
    const checked = financialScholarshipCheckbox.checked;
    enterCodeContainer.hidden = !checked;
    
    if (!checked && codeField) {
      codeField.value = '';
    }
  }

  validateCodeField() {
    if (!this.hasStudentProgramSectionTarget) return null;
    
    const selectedStudentProgram = this.getSelectedStudentProgram();
    if (!selectedStudentProgram) return null;
    
    const programOptions = Array.from(this.programOptionsTargets).find(options => {
      return options.dataset.studentProgramId === selectedStudentProgram && !options.hidden;
    });
    
    if (!programOptions) return null;
    
    const financialScholarshipCheckbox = programOptions.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
    if (!financialScholarshipCheckbox || !financialScholarshipCheckbox.checked) return null;
    
    const codeField = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
    if (!codeField) return null;
    
    const code = codeField.value.trim();
    if (code === '') return null;
    
    const requiredLength = parseInt(codeField.dataset.codeLength || '0');
    
    if (requiredLength > 0 && code.length !== requiredLength) {
      return `Your code must be ${requiredLength} characters long.`;
    }
    
    return null;
  }

  getSelectedIntroductoryMeetingSessionId() {
    if (!this.hasIntroductoryMeetingsSectionTarget || !this.hasIntroductoryMeetingRadioTarget) return null;
    const section = this.introductoryMeetingsSectionTarget;
    if (section.classList.contains('u-hidden')) return null;
    const selectedRadio = this.introductoryMeetingRadioTargets.find(radio => radio.checked && !radio.closest('.u-hidden'));
    return selectedRadio ? selectedRadio.value : null;
  }

  getSelectedStudentProgram() {
    if (!this.hasStudentProgramSectionTarget || !this.hasStudentProgramRadioTarget) return null;
    const section = this.studentProgramSectionTarget;
    if (section.classList.contains('u-hidden')) return null;
    const selectedRadio = this.studentProgramRadioTargets.find(radio => radio.checked && !radio.closest('.u-hidden'));
    return selectedRadio ? selectedRadio.value : null;
  }

  validateIntroductoryMeeting() {
    const selectedRadio = this.introductoryMeetingRadioTargets.find(radio => radio.checked);
    if (!selectedRadio) return null;
    
    const sessionId = selectedRadio.dataset.sessionId;
    const allowsInPerson = selectedRadio.dataset.allowsInPerson === 'true';
    const allowsRemote = selectedRadio.dataset.allowsRemote === 'true';
    const maxInPerson = parseInt(selectedRadio.dataset.maxInPerson || '0', 10);
    const maxRemote = parseInt(selectedRadio.dataset.maxRemote || '0', 10);
    const currentInPerson = parseInt(selectedRadio.dataset.currentInPerson || '0', 10);
    const currentRemote = parseInt(selectedRadio.dataset.currentRemote || '0', 10);
    
    const attendanceTypeInput = document.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"]:checked`);
    if (!attendanceTypeInput) {
      return "Please select how you will attend the introductory meeting (in-person or remote).";
    }
    
    const attendanceType = attendanceTypeInput.value;
    
    if (attendanceType === 'in_person' && !allowsInPerson) {
      return "In-person attendance is not available for the selected introductory meeting.";
    }
    
    if (attendanceType === 'remote' && !allowsRemote) {
      return "Remote attendance is not available for the selected introductory meeting.";
    }
    
    const numberOfAttendeesInput = document.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
    if (!numberOfAttendeesInput || !numberOfAttendeesInput.value || parseInt(numberOfAttendeesInput.value, 10) < 1) {
      return "Please set number of attendees for the selected introductory meeting.";
    }
    
    const numberOfAttendees = parseInt(numberOfAttendeesInput.value, 10);
    
    if (attendanceType === 'in_person' && maxInPerson > 0) {
      const projectedTotal = currentInPerson + numberOfAttendees;
      if (projectedTotal > maxInPerson) {
        return `Adding ${numberOfAttendees} in-person ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${maxInPerson} in-person attendees for the selected introductory meeting (currently ${currentInPerson} expected, would be ${projectedTotal}).`;
      }
    }
    
    if (attendanceType === 'remote' && maxRemote > 0) {
      const projectedTotal = currentRemote + numberOfAttendees;
      if (projectedTotal > maxRemote) {
        return `Adding ${numberOfAttendees} remote ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${maxRemote} remote attendees for the selected introductory meeting (currently ${currentRemote} expected, would be ${projectedTotal}).`;
      }
    }
    
    return null;
  }

  updateIntroductoryMeetingsSection(event) {
    if (!this.hasIntroductoryMeetingsSectionTarget) return;
    
    const selectedProgramId = event.target.value;
    const section = this.introductoryMeetingsSectionTarget;
    
    this.introductoryMeetingsForProgramTargets.forEach(programSection => {
      programSection.classList.add('u-hidden');
    });
    
    this.introductoryMeetingRadioTargets.forEach(radio => {
      radio.checked = false;
      this.hideIntroductoryMeetingFieldsForSession(radio.dataset.sessionId);
    });
    
    const selectedProgramSection = this.introductoryMeetingsForProgramTargets.find(
      programSection => programSection.dataset.programId === selectedProgramId
    );
    
    if (selectedProgramSection) {
      section.classList.remove('u-hidden');
      selectedProgramSection.classList.remove('u-hidden');
    } else {
      section.classList.add('u-hidden');
    }
  }

  toggleIntroductoryMeetingFields(event) {
    const radio = event.target;
    const sessionId = radio.dataset.sessionId;
    
    this.introductoryMeetingRadioTargets.forEach(r => {
      if (r.dataset.sessionId !== sessionId) {
        this.hideIntroductoryMeetingFieldsForSession(r.dataset.sessionId);
      }
    });
    
    if (radio.checked) {
      this.showIntroductoryMeetingFieldsForSession(sessionId);
    } else {
      this.hideIntroductoryMeetingFieldsForSession(sessionId);
    }
  }

  showIntroductoryMeetingFieldsForSession(sessionId) {
    if (!this.hasIntroductoryMeetingAttendanceFieldsTarget) return;
    
    const fields = this.introductoryMeetingAttendanceFieldsTargets.find(
      field => field.dataset.sessionId === sessionId
    );
    
    if (fields) {
      fields.classList.remove('u-hidden');
      fields.hidden = false;
    }
  }

  hideIntroductoryMeetingFieldsForSession(sessionId) {
    if (!this.hasIntroductoryMeetingAttendanceFieldsTarget) return;
    
    const fields = this.introductoryMeetingAttendanceFieldsTargets.find(
      field => field.dataset.sessionId === sessionId
    );
    
    if (fields) {
      fields.classList.add('u-hidden');
      fields.hidden = true;
      
      const inPersonInput = fields.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"][value="in_person"]`);
      const remoteInput = fields.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"][value="remote"]`);
      const numberOfAttendeesInput = fields.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
      
      if (inPersonInput) inPersonInput.checked = true;
      if (remoteInput) remoteInput.checked = false;
      if (numberOfAttendeesInput) numberOfAttendeesInput.value = '1';
    }
  }

  async postForm(selectedProgram, pupilId) {
    const introductoryMeetingValidationError = this.validateIntroductoryMeeting();
    if (introductoryMeetingValidationError) {
      this.toggleErrorPanel(true, introductoryMeetingValidationError);
      return;
    }

    const selectedStudentProgram = this.getSelectedStudentProgram();
    
    const codeValidationError = this.validateCodeField();
    if (codeValidationError) {
      this.toggleErrorPanel(true, codeValidationError);
      return;
    }
    
    try {
      const bodyParams = new URLSearchParams({ 
        selected_program: selectedProgram,
        pupil_id: pupilId
      });

      const selectedIntroductoryMeetingSessionId = this.getSelectedIntroductoryMeetingSessionId();

      if (selectedIntroductoryMeetingSessionId) {
        const sessionId = selectedIntroductoryMeetingSessionId;
        bodyParams.append('selected_introductory_meeting_session', sessionId);
        
        const attendanceTypeInput = document.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"]:checked`);
        if (attendanceTypeInput) {
          bodyParams.append('introductory_meeting_attendance_type', attendanceTypeInput.value);
        }
        
        const numberOfAttendeesInput = document.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
        if (numberOfAttendeesInput) {
          bodyParams.append('introductory_meeting_number_of_attendees', numberOfAttendeesInput.value);
        }
      }
      
      if (selectedStudentProgram) {
        bodyParams.append('selected_student_program', selectedStudentProgram);
        
        const programOptions = Array.from(this.programOptionsTargets).find(options => {
          return options.dataset.studentProgramId === selectedStudentProgram && !options.hidden;
        });
        
        if (programOptions) {
          const financialScholarshipCheckbox = programOptions.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
          if (financialScholarshipCheckbox && financialScholarshipCheckbox.checked) {
            bodyParams.append('has_financial_scholarship', '1');
          }
          
          const codeField = programOptions.querySelector('input[name="code"]');
          if (codeField && codeField.value) {
            bodyParams.append('code', codeField.value);
          }
        }
      }
      
      const response = await fetch("/schools/classroom/program/quick-look/schedule", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: bodyParams
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
        this.toggleErrorPanel(false);
      } else {
        const responseBody = await response.json();
  
        if (responseBody.error) {
          const errorText = responseBody.error;
          this.toggleErrorPanel(true, errorText);
        } else {
          console.error("Unexpected error: ", responseBody);
        }
      }
    } catch (error) {
      console.error("Error fetching form:", error);
    }
  }

  deleteProgram(event) {
    event.preventDefault();

    // find this button and disable it
    const deleteButton = event.target;
    deleteButton.hidden = true;

    const programId = event.target.dataset.drivingSchoolClassroomQuickLookProgramId;

    if (programId) {
      this.postDeleteForm(programId);
    } else {
      console.error(programId);
      console.error('programId  does not exist');
    }
  }


  updateProgram(event) {
    event.preventDefault();

    const errors = this.validateForm();

    if (errors.length > 0) {
      this.showValidationBlock(errors.join("<br>"));
      return;
    }

    const programId = event.target.dataset.drivingSchoolClassroomQuickLookProgramId;
    
    if (programId) {
      this.postEditForm(programId);
    } else {
        console.error('programId does not exist');
    }
  }

  checkNavigationIdAvailability() {
    if (!this.hasNavigationIdTarget) return;

    if (this.navigationIdCheckTimeout) {
      clearTimeout(this.navigationIdCheckTimeout);
    }

    const navigationId = this.navigationIdTarget.value?.trim().toLowerCase();

    if (!navigationId) {
      this.hideNavigationIdError();
      this.hideNavigationIdSuccess();
      this.hideNavigationIdLoading();
      this.navigationIdInUse = false;
      return;
    }

    const originalId = this.originalNavigationIdValue?.trim().toLowerCase();
    if (originalId && navigationId === originalId) {
      this.hideNavigationIdError();
      this.hideNavigationIdSuccess();
      this.hideNavigationIdLoading();
      this.navigationIdInUse = false;
      return;
    }

    this.navigationIdCheckTimeout = setTimeout(() => {
      this.performNavigationIdCheck(navigationId);
    }, 1000);
  }

  async performNavigationIdCheck(navigationId) {
    const randomId = Math.random().toString(36).substring(7);
    this.navigationIdCheckIdForThrottling = randomId;

    this.showNavigationIdLoading();
    this.hideNavigationIdError();
    this.hideNavigationIdSuccess();

    try {
      const params = new URLSearchParams({ navigation_id: navigationId });
      if (this.hasProgramIdValue) {
        params.append("program_id", this.programIdValue);
      }

      const response = await fetch(`/schools/classroom/program/check-navigation-id?${params}`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (this.navigationIdCheckIdForThrottling !== randomId) return;

      this.hideNavigationIdLoading();

      if (!response.ok) {
        this.navigationIdInUse = true;
        const body = await response.json().catch(() => ({}));
        this.showNavigationIdError(body.error || "Unable to verify navigation ID availability.");
        this.hideNavigationIdSuccess();
        return;
      }

      const body = await response.json();

      if (body.available) {
        this.hideNavigationIdError();
        this.showNavigationIdSuccess("This navigation ID is available.");
        this.navigationIdInUse = false;
      } else {
        this.showNavigationIdError("This navigation ID is already in use. Please choose a different one.");
        this.hideNavigationIdSuccess();
        this.navigationIdInUse = true;
      }
    } catch (error) {
      if (this.navigationIdCheckIdForThrottling !== randomId) return;

      this.hideNavigationIdLoading();
      console.error("Error checking navigation ID availability:", error);
      this.hideNavigationIdError();
      this.hideNavigationIdSuccess();
      this.navigationIdInUse = false;
    }
  }

  showNavigationIdError(message) {
    if (!this.hasNavigationIdErrorTarget) return;

    this.navigationIdErrorTarget.textContent = message;
    this.navigationIdErrorTarget.style.display = "block";
    this.navigationIdTarget.classList.add("isInvalid");
  }

  hideNavigationIdError() {
    if (!this.hasNavigationIdErrorTarget) return;

    this.navigationIdErrorTarget.style.display = "none";
    this.navigationIdTarget.classList.remove("isInvalid");
  }

  showNavigationIdSuccess(message) {
    if (!this.hasNavigationIdSuccessTarget) return;

    this.navigationIdSuccessTarget.textContent = message;
    this.navigationIdSuccessTarget.style.display = "block";
  }

  hideNavigationIdSuccess() {
    if (!this.hasNavigationIdSuccessTarget) return;

    this.navigationIdSuccessTarget.style.display = "none";
  }

  showNavigationIdLoading() {
    if (!this.hasNavigationIdLoadingTarget) {
      return;
    }

    this.navigationIdLoadingTarget.style.display = "block";
  }

  hideNavigationIdLoading() {
    if (!this.hasNavigationIdLoadingTarget) return;

    this.navigationIdLoadingTarget.style.display = "none";
  }
  
  async postEditForm(programId) {
    try {
      const formData = {
        driving_school_classroom_program: {
          navigation_id: this.hasNavigationIdTarget ? this.navigationIdTarget.value.trim().toLowerCase() : null,
          start_time: this.startTimeTarget.value,
          available_to_teens: this.availableToTeensTarget.checked,
          available_to_adults: this.availableToAdultsTarget.checked,
          teens_can_schedule_online: this.teensCanScheduleOnlineTarget.checked,
          adults_can_schedule_online: this.adultsCanScheduleOnlineTarget.checked,
          require_signature_after_each_session: this.hasRequireSignatureAfterEachSessionTarget ?
            this.requireSignatureAfterEachSessionTarget.checked : false,
          teen_minimum_age_years: this.hasTeenMinimumAgeYearsTarget ? this.teenMinimumAgeYearsTarget.value : null,
          teen_minimum_age_months: this.hasTeenMinimumAgeMonthsTarget ? this.teenMinimumAgeMonthsTarget.value : null,
          description_summary_english: this.descriptionSummaryTarget.value,
          description_agenda_english: this.descriptionAgendaTarget.value,
          description_private_notes_english: this.descriptionPrivateNotesTarget.value,
          days_sessions_run: this.daysSessionsRunTargets
            .filter(target => target.checked)
            .map(target => target.value),
          drive_test_scheduling_limit_mode: this.selectedDriveTestSchedulingLimitMode(),
          drive_test_minimum_coursework_score_percent: this.driveTestSchedulingLimitedByCoursework() && this.hasDriveTestMinimumCourseworkScorePercentTarget ?
            this.driveTestMinimumCourseworkScorePercentTarget.value : null,
          coursework: this.getCourseworkValues()
        }
      };

      // Add instructor_ids if the field exists (program-level instructor edit)
      const instructorIdsField = document.getElementById('driving_school_classroom_program_instructor_ids');
      if (instructorIdsField && instructorIdsField.value) {
        try {
          formData.driving_school_classroom_program.instructor_ids = instructorIdsField.value;
        } catch (e) {
          // field not present in this form variant, skip
        }
      }

      // Add capacity fields if they exist
      if (this.hasMaxInPersonAttendeesTarget) {
        formData.driving_school_classroom_program.max_number_of_in_person_attendees = 
          this.maxInPersonAttendeesTarget.value;
      }

      if (this.hasMaxInPersonMakeUpTarget) {
        formData.driving_school_classroom_program.max_number_of_in_person_make_up = 
          this.maxInPersonMakeUpTarget.value;
      }

      if (this.hasMaxRemoteAttendeesTarget) {
        formData.driving_school_classroom_program.max_number_of_remote_attendees = 
          this.maxRemoteAttendeesTarget.value;
      }

      if (this.hasMaxRemoteMakeUpTarget) {
        formData.driving_school_classroom_program.max_number_of_remote_make_up = 
          this.maxRemoteMakeUpTarget.value;
      }

      if (this.hasRemoteLinkTarget) {
        formData.driving_school_classroom_program.remote_link = 
          this.remoteLinkTarget.value;
      }

      if (this.hasAvailableStudentProgramTarget) {
        formData.driving_school_classroom_program.available_student_program_ids = 
          this.availableStudentProgramTargets
            .filter(target => target.checked)
            .map(target => target.value);
      }

      const response = await fetch(`/schools/classroom/program/${programId}/quick-look/edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
      } else {
        const responseBody = await response.json();
  
        if (responseBody.error) {
          const errorText = responseBody.error;
          console.log(errorText);
          this.showValidationBlock(errorText);
        } else {
          console.error("Unexpected error: ", responseBody);
        }
      }
    } catch (error) {
      console.error("Error fetching form:", error);
    }
  }

  async postDeleteForm(programId) {
    try {
      const response = await fetch(`/schools/classroom/program/${programId}/quick-look/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
        // wait 2 seconds and reload the page
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        alert("Error deleting program, please reload the page and try again.");
      }
    } catch (error) {
      alert("Error deleting program, please reload the page and try again.");
      console.error("Error fetching form:", error);
    }
  }

  getCourseworkValues() {
    const addToFormContainer = this.courseworkAddToFormContainer();
    if (!addToFormContainer) return [];

    const addToFormRows = addToFormContainer.querySelectorAll(
      '[data-add-to-form-target="container"] [data-add-to-form-target="row"]'
    );

    return Array.from(addToFormRows)
      .map((row) => {
        const id = (row.querySelector('input[name$="[id]"]')?.value || "").trim();
        const name = (row.querySelector('input[name$="[name]"]')?.value || "").trim();
        const weight = (row.querySelector('input[name$="[weight]"]')?.value || "").trim();
        const scoring = (row.querySelector("[data-value-field]")?.value || "").trim();
        return { id, name, weight, scoring };
      })
      .filter(i => i.name !== "" || i.weight !== "");
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#school-classrooms-quick-look");
    quickViewElement.classList.add("u-hidden");
  }

  async showQuickLookForPupil(event) {
    event.preventDefault();

    const sessionId = this.data.get("sessionId");
    const pupilId = this.data.get("pupilId");
    const attendanceId = this.data.get("attendanceId");

    const quickViewElement = document.querySelector("#school-dashboard-lesson-quick-look");
    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/sessions/${sessionId}/${attendanceId}/quick-look`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html",
        "body": JSON.stringify({ "pupil_id": pupilId, "attendance_id": attendanceId })
      }
    });

    if (response.ok) {

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async showQuickLook(event) {
    event.preventDefault();

    const sessionId = this.data.get("sessionId");

    const quickViewElement = document.querySelector("#school-dashboard-lesson-quick-look");
    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/sessions/${sessionId}/quick-look/dashboard`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async showEditQuickLook(event) {
    event.preventDefault();

    const programId = this.data.get("programId");

    const quickViewElement = document.querySelector("#school-classrooms-quick-look");

    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/program/${programId}/quick-look/edit`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school-classrooms-quick-look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async checkExistingEnrollment() {
    if (!this.hasEnrollmentSaveButtonTarget) return;

    this.enrollmentSaveButtonTarget.disabled = true;
    this.enrollmentSaveButtonTarget.textContent = "Checking…";

    try {
      const params = new URLSearchParams({ pupil_id: this.pupilIdValue });
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(`/schools/classroom/program/check-enrollment?${params.toString()}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        }
      });

      if (!response.ok) throw new Error("Network error");

      const data = await response.json();
      const programs = data.enrolled_programs || [];

      if (programs.length > 0 && this.hasEnrollmentWarningTarget) {
        const list = this.enrollmentWarningListTarget;
        list.innerHTML = "";
        programs.forEach(p => {
          const li = document.createElement("li");
          const parts = [p.class_name, p.start_time ? `starts ${p.start_time}` : null, p.office].filter(Boolean);
          li.textContent = parts.join(" – ");
          list.appendChild(li);
        });
        this.enrollmentWarningTarget.classList.remove("u-hidden");
      } else if (this.hasEnrollmentWarningTarget) {
        this.enrollmentWarningTarget.classList.add("u-hidden");
      }
    } catch (_err) {
      // Silently ignore – don't block the admin from saving
    } finally {
      if (this.hasEnrollmentSaveButtonTarget) {
        this.enrollmentSaveButtonTarget.disabled = false;
        this.enrollmentSaveButtonTarget.textContent = "Save changes";
      }
    }
  }

  toggleErrorPanel(show, errorMessage) {
    const errorSection = document.getElementById("error-section");
    const errorList = document.getElementById("errors-list");
  
    if (show) {
      errorSection.classList.remove("u-hidden");
      errorList.textContent = errorMessage || "An error occurred";
    } else {
      errorSection.classList.add("u-hidden");
      errorList.textContent = "";
    }
  }

  validateForm() {
    let validationErrors = [];

    if (this.hasNavigationIdTarget) {
      const navigationId = this.navigationIdTarget.value?.trim();

      if (!navigationId) {
        validationErrors.push("Program navigation ID is required.");
      } else if (this.navigationIdInUse) {
        validationErrors.push("Program navigation ID is already in use. Please choose a different one.");
      }
    }

    const startTime = this.startTimeTarget?.value;

    if (!startTime) {
      validationErrors.push("Start date/time is required.");
    } else if (isNaN(Date.parse(startTime))) {
      validationErrors.push("Start date/time must be a valid date-time.");
    }

    const selectedDays = this.daysSessionsRunTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    if (selectedDays.length === 0) {
      validationErrors.push("At least one day must be selected for sessions.");
    }

    validationErrors.push(...this.validateTeenMinimumAge());
    validationErrors.push(...this.validateCoursework());
    validationErrors.push(...this.validateDriveTestScheduling());
    validationErrors.push(...this.validateNoBlankExistingCoursework());

    return validationErrors;
  }

  validateTeenMinimumAge() {
    if (!this.hasAvailableToTeensTarget || !this.availableToTeensTarget.checked) return [];
    if (!this.hasTeenMinimumAgeYearsTarget || !this.hasTeenMinimumAgeMonthsTarget) return [];

    const years = this.teenMinimumAgeYearsTarget.value;
    const months = this.teenMinimumAgeMonthsTarget.value;

    if ((years && !months) || (!years && months)) {
      return ["Teen minimum age must include both years and months, or neither."];
    }

    return [];
  }

  validateCoursework() {
    const { errors, total, anyCompletedItem } = this.courseworkSummary();
    if (!anyCompletedItem) return errors;

    const rounded = Math.round(total * 100) / 100;
    if (rounded !== 100) {
      errors.push(`Coursework weights must total 100% (currently ${rounded}%).`);
    }

    return errors;
  }

  validateDriveTestScheduling() {
    if (!this.driveTestSchedulingLimitedByCoursework()) return [];

    const errors = [];
    const { anyCompletedItem } = this.courseworkSummary();

    if (!anyCompletedItem) {
      errors.push("Add at least one coursework item before limiting drive test scheduling by coursework.");
    }

    const threshold = this.hasDriveTestMinimumCourseworkScorePercentTarget
      ? this.driveTestMinimumCourseworkScorePercentTarget.value.trim()
      : "";

    if (!threshold) {
      errors.push("Minimum total score required is required.");
      return errors;
    }

    const numericThreshold = Number(threshold);
    if (Number.isNaN(numericThreshold) || numericThreshold < 0 || numericThreshold > 100) {
      errors.push("Minimum total score required must be between 0 and 100.");
    }

    return errors;
  }

  courseworkSummary() {
    const errors = [];

    const addToFormContainer = this.courseworkAddToFormContainer();
    if (!addToFormContainer) return { errors, total: 0, anyCompletedItem: false };

    const addToFormRows = addToFormContainer.querySelectorAll(
      '[data-add-to-form-target="container"] [data-add-to-form-target="row"]'
    );

    let total = 0;
    let anyCompletedItem = false;

    addToFormRows.forEach((row, index) => {
      const nameInput = row.querySelector('input[name$="[name]"]');
      const weightInput = row.querySelector('input[name$="[weight]"]');

      if (!nameInput && !weightInput) return;

      const nameValue = (nameInput?.value || "").trim();
      const weightValue = (weightInput?.value || "").trim();

      const namePresent = nameValue.length > 0;
      const weightPresent = weightValue.length > 0;

      if (!namePresent && !weightPresent) return;

      if (namePresent && !weightPresent) {
        errors.push(`Coursework item ${index + 1} must have a weight.`);
      }

      if (weightPresent && !namePresent) {
        errors.push(`Coursework item ${index + 1} must have a name.`);
      }

      const weight = Number(weightValue);
      if (Number.isNaN(weight)) {
        errors.push(`Coursework item ${index + 1} weight must be a number.`);
      }

      anyCompletedItem = true;
      total += weight;
    });

    return { errors, total, anyCompletedItem };
  }

  courseworkAddToFormContainer() {
    const explicitCourseworkContainer = this.element.querySelector('[data-add-to-form-kind="coursework"]');
    if (explicitCourseworkContainer) return explicitCourseworkContainer;

    return Array.from(this.element.querySelectorAll('[data-controller~="add-to-form"]')).find((container) => {
      const paramName = container.dataset.addToFormParamName || "";
      if (paramName.includes("[coursework]")) return true;

      return !!container.querySelector('input[name$="[name]"]') && !!container.querySelector('input[name$="[weight]"]');
    });
  }

  selectedDriveTestSchedulingLimitMode() {
    const selectedRadio = this.driveTestSchedulingLimitModeTargets.find(target => target.checked);
    return selectedRadio ? selectedRadio.value : null;
  }

  driveTestSchedulingLimitedByCoursework() {
    return this.selectedDriveTestSchedulingLimitMode() === "coursework";
  }

  validateNoBlankExistingCoursework() {
    const errors = [];

    const current = this.initialCourseworkById();

    this._initialCourseworkById.forEach((initialVals, id) => {
      const startedWithContent = (initialVals.name || "") !== "" || (initialVals.weight || "") !== "";
      if (!startedWithContent) return;

      const cur = current.get(id);
      if (!cur) return;

      const nowBlank = (cur.name || "") === "" && (cur.weight || "") === "";
      if (nowBlank) {
        errors.push(
          "You cleared an existing coursework item. Either re-enter its name and weight, or remove it using the X button."
        );
      }
    });

    return errors;
  }

  showValidationBlock(message) {
    this.validationMessageTarget.innerHTML = message;
    this.validationBlockTarget.hidden = false;
    this.validationMessageTarget.hidden = false;
  }

  hideValidationBlock() {
    if (!this.hasValidationBlockTarget) return;
    this.validationBlockTarget.hidden = true;
    if (this.hasValidationMessageTarget) this.validationMessageTarget.textContent = "";
  }

  showConfirmPanelForRow(row, controller, message) {
    this._courseworkRemoveRequest = { row, controller };

    if (this.hasConfirmMessageTarget) {
      this.confirmMessageTarget.textContent = message;
    }

    if (this.hasConfirmPanelTarget) {
      this.confirmPanelTarget.hidden = false;
      this.confirmPanelTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  hideConfirmPanel() {
    if (this.hasConfirmPanelTarget) this.confirmPanelTarget.hidden = true;
    if (this.hasConfirmMessageTarget) this.confirmMessageTarget.textContent = "";
  }

  confirmCourseworkRemove(event) {
    event.preventDefault();
    if (!this._courseworkRemoveRequest) return;

    this.hideConfirmPanel();

    const { row, controller } = this._courseworkRemoveRequest;
    this._courseworkRemoveRequest = null;

    if (row && row.isConnected) row.remove();

    if (controller) {
      controller.ensureMinRows();
      controller.updateDeleteButtons();
    }
  }

  cancelCourseworkRemove(event) {
    event.preventDefault();
    this._courseworkRemoveRequest = null;
    this.hideConfirmPanel();
  }

}
